import {useState} from 'react';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useWorkspace} from '../hooks/useWorkspace';
import {dataClient} from '../data/dataClient';
import {TaskCard, PlanCard} from '../components/WorkComponents';
import QuotationModal from '../components/QuotationModal';
import {workspacePath} from '../services/paths.js';

const {
  FiBriefcase,
  FiPlus,
  FiArrowLeft,
  FiCheckSquare,
  FiFileText,
  FiLayers,
  FiX,
  FiPrinter
} = FiIcons;

export default function Projects() {
  const state = useWorkspace();
  const projects = state.projects || [];
  const tasks = state.tasks || [];
  const plans = state.plans || [];
  const briefs = state.adCampaignBriefs || [];

  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectClient, setProjectClient] = useState('');
  const [projectDesc, setProjectDesc] = useState('');
  const [quotePlan, setQuotePlan] = useState(null);

  const selected = projects.find((p) => p.id === selectedProjectId);

  async function handleCreateProject(e) {
    e.preventDefault();
    if (!projectName.trim()) return;

    const newProj = await dataClient.create('projects', {
      name: projectName.trim(),
      client: projectClient.trim() || 'Internal Initiative',
      description: projectDesc.trim(),
      status: 'active'
    });

    setSelectedProjectId(newProj.id);
    setProjectName('');
    setProjectClient('');
    setProjectDesc('');
    setShowCreate(false);
  }

  // If a specific project is selected, render the Project Lens View
  if (selected) {
    const projectTasks = tasks.filter((t) => t.project === selected.id || t.projectId === selected.id);
    const projectPlans = plans.filter((p) => p.project === selected.id);
    const projectBriefs = briefs.filter((b) => b.project === selected.id);

    return (
      <div className="page">
        <header className="page-header">
          <button
            type="button"
            className="button ghost compact project-back-row"
            onClick={() => setSelectedProjectId(null)}
          >
            <SafeIcon icon={FiArrowLeft} /> All projects
          </button>
          <span className="eyebrow">Project Lens · {selected.client || 'Client'}</span>
          <div className="inline spread">
            <div>
              <h1>{selected.name}</h1>
              <p>{selected.description || 'A unified lens across tasks, plans, and briefs for this engagement.'}</p>
            </div>
            <span className={`status ${selected.status || 'active'}`}>{selected.status || 'active'}</span>
          </div>
        </header>

        {/* Project Summary Metrics */}
        <div className="stats-grid">
          <article>
            <small>Tagged tasks</small>
            <b>{projectTasks.length}</b>
            <span>{projectTasks.filter((t) => t.status === 'done').length} completed</span>
          </article>
          <article>
            <small>Decision plans</small>
            <b>{projectPlans.length}</b>
            <span>{projectPlans.filter((p) => p.status === 'approved').length} approved</span>
          </article>
          <article>
            <small>Campaign briefs</small>
            <b>{projectBriefs.length}</b>
            <span>Creative messaging</span>
          </article>
        </div>

        {/* Tasks for this Project */}
        <section className="panel project-section">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Execution</span>
              <h3>Project Tasks</h3>
            </div>
            <Link to={workspacePath('tasks', state)} className="button ghost compact">
              All tasks <SafeIcon icon={FiCheckSquare} />
            </Link>
          </div>

          {projectTasks.length === 0 ? (
            <p className="muted">No tasks tagged to this project yet.</p>
          ) : (
            <div className="task-board project-task-board">
              {projectTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  plan={plans.find((p) => p.id === task.planId)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Plans for this Project */}
        <section className="panel project-section">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Strategy &amp; Briefs</span>
              <h3>Project Plans</h3>
            </div>
          </div>

          {projectPlans.length === 0 ? (
            <p className="muted">No plans tagged to this project yet.</p>
          ) : (
            <div className="card-grid">
              {projectPlans.map((plan) => (
                <article className="plan-card" key={plan.id}>
                  <div className="inline spread">
                    <span className={`status ${plan.status}`}>{plan.status}</span>
                    <button
                      type="button"
                      className="button ghost compact project-quote-button"
                      onClick={() => setQuotePlan(plan)}
                    >
                      <SafeIcon icon={FiPrinter} /> Quote export
                    </button>
                  </div>
                  <h3>{plan.title}</h3>
                  <p><SafeIcon icon={FiFileText} /> {plan.module} · v{plan.version || 1}</p>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Campaign Briefs for this Project */}
        {projectBriefs.length > 0 && (
          <section className="panel">
            <div className="section-heading">
              <div>
                <span className="eyebrow">Creative deliverables</span>
                <h3>Ad Campaign Briefs</h3>
              </div>
            </div>
            <div className="card-grid">
              {projectBriefs.map((brief) => (
                <article className="plan-card" key={brief.id}>
                  <div className="inline spread">
                    <span className={`status ${brief.status}`}>{brief.status}</span>
                  </div>
                  <h3>{brief.title}</h3>
                  <p className="brief-key-message">“{brief.keyMessage}”</p>
                </article>
              ))}
            </div>
          </section>
        )}

        {quotePlan && (
          <QuotationModal plan={quotePlan} onClose={() => setQuotePlan(null)} />
        )}
      </div>
    );
  }

  // All Projects List View
  return (
    <div className="page">
      <header className="page-header">
        <span className="eyebrow">Projects</span>
        <div className="inline spread">
          <div>
            <h1>A lens over work your bots are already doing.</h1>
            <p>
              Tag conversations, tasks, and plans to a project to keep one engagement in view — without duplicating your AI workforce.
            </p>
          </div>
          <button
            type="button"
            className="button primary compact"
            onClick={() => setShowCreate(true)}
          >
            <SafeIcon icon={FiPlus} />
            New project
          </button>
        </div>
      </header>

      {/* New Project Modal */}
      {showCreate && (
        <div className="add-page-modal-backdrop" onClick={() => setShowCreate(false)}>
          <div className="add-page-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="section-heading">
              <h3>Create project lens</h3>
              <button type="button" className="icon-button" onClick={() => setShowCreate(false)}>
                <SafeIcon icon={FiX} />
              </button>
            </div>
            <form onSubmit={handleCreateProject} className="project-form">
              <label className="filter">
                Project name
                <input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g. Autumn Collection Launch"
                  required
                />
              </label>
              <label className="filter">
                Client / Initiative
                <input
                  value={projectClient}
                  onChange={(e) => setProjectClient(e.target.value)}
                  placeholder="e.g. Acme Retail or DTC Growth"
                />
              </label>
              <label className="filter">
                Description
                <textarea
                  value={projectDesc}
                  onChange={(e) => setProjectDesc(e.target.value)}
                  placeholder="Overview of the engagement or goal…"
                />
              </label>
              <div className="wizard-actions">
                <button type="button" className="button ghost" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="button primary" disabled={!projectName.trim()}>
                  Create project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Projects Grid */}
      <div className="card-grid projects-grid">
        {projects.map((proj) => {
          const projTasks = tasks.filter((t) => t.project === proj.id || t.projectId === proj.id);
          const projPlans = plans.filter((p) => p.project === proj.id);
          return (
            <article
              key={proj.id}
              className="panel project-card"
              onClick={() => setSelectedProjectId(proj.id)}
            >
              <div className="inline spread">
                <span className="eyebrow">{proj.client || 'Project'}</span>
                <span className={`status ${proj.status || 'active'}`}>{proj.status || 'active'}</span>
              </div>
              <h3>{proj.name}</h3>
              <p>
                {proj.description || 'Click to view tagged tasks, decisions, and briefs.'}
              </p>
              <div className="task-meta">
                <span><SafeIcon icon={FiCheckSquare} /> {projTasks.length} tasks</span>
                <span><SafeIcon icon={FiFileText} /> {projPlans.length} plans</span>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
